import React, { useMemo } from 'react';

import { useMutation } from '@tanstack/react-query';

import FormContainer from '../../../../FormContainer';
import Table from '../../../../../Table';
import DownloadDocumentButton from '../../../../../DownloadDocumentButton';

import useClaimDocumentForm from '../../../../../../forms/loan/claimDocument';
import DocumentService from '../../../../../../services/DocumentService';
import UploadService from '../../../../../../services/UploadService';
import ClaimService from '../../../../../../services/ClaimService';
import { useUiContext } from '../../../../../../context/UiContext';

export default function ClaimDocuments({ isPhoto, claim, loanId, refetch }) {
  const { showToast } = useUiContext();

  const { mutate: updateDocumentDescirption } = useMutation({
    mutationFn: async ({ documentId, description, document }) => {
      return DocumentService.updateDocument({
        documentId,
        formValues: { ...document, description },
      });
    },
    onSuccess: () => {
      showToast({
        text: 'Document description updated successfully',
        type: 'success',
      });
      refetch();
    },
    onError: (error) => {
      console.log(error);
      showToast({
        text: error?.response?.data?.message || 'Something went wrong',
        type: 'error',
      });
    },
  });

  const cols = useMemo(
    () => [
      {
        title: 'Description',
        keys: isPhoto ? ['_photo.description'] : ['_document.description'],
        type: 'editableWithButtons',
        inputType: 'text',
        onSubmit: (row, value) => {
          updateDocumentDescirption({
            documentId: isPhoto ? row._photo.id : row._document.id,
            description: value,
            document: isPhoto ? row._photo : row._document,
          });
        },
      },
      {
        title: '',
        type: 'component',
        component: DownloadDocumentButton,
        props: (row) => ({
          doc: row._document || row._photo,
        }),
      },
    ],
    [isPhoto, updateDocumentDescirption]
  );

  const { mutate: upload, isPending } = useMutation({
    mutationFn: async (formValues) => {
      const uploadFile = async (file) => {
        let uploadedFile;

        if (isPhoto) {
          uploadedFile = await UploadService.uploadImage(file);
        } else {
          uploadedFile = await UploadService.uploadAttachment(file);
        }

        const doc = await DocumentService.createDocument({
          formValues: {
            private_document: uploadedFile,
            status: 'accepted',
            description: `Claim - ${claim.date_of_loss}`,
            category: 'user',
          },
          loanId,
        });

        return doc;
      };

      const docs = await Promise.all(
        formValues.files.map((file) => uploadFile(file))
      );
      const docIds = docs.map((doc) => doc.id);

      const documentIds =
        claim.document_id
          ?.map((dc) => dc?._document?.id)
          ?.filter((dc) => !!dc) || [];
      const photoIds =
        claim.photo_id?.map((ph) => ph?._photo?.id)?.filter((ph) => !!ph) || [];

      const values = { ...claim };

      if (isPhoto) {
        values.photo_id = [...photoIds, ...docIds];
        values.document_id = documentIds;
      } else {
        values.document_id = [...documentIds, ...docIds];
        values.photo_id = photoIds;
      }

      return ClaimService.updateClaim({
        claimId: claim.id,
        formValues: values,
      });
    },
    onSuccess: () => {
      showToast({
        text: 'Document uploaded successfully',
        type: 'success',
      });

      refetch();
    },
    onError: (error) => {
      console.log(error);
      showToast({
        text: error?.response?.data?.message || 'Something went wrong',
        type: 'error',
      });
    },
  });

  const form = useClaimDocumentForm(upload, isPhoto);

  return (
    <div>
      <h2 className="text-2xl text-slate-800 dark:text-slate-100 font-bold flex justify-between w-full">
        {isPhoto
          ? 'Upload photos to this claim'
          : 'Upload documents to this claim'}
      </h2>
      <div className="mb-10">
        <FormContainer
          form={form}
          noBillComId
          disabled={!form.formik.values.files?.length}
          isSubmitting={isPending}
        />
      </div>
      <Table
        rows={isPhoto ? claim?.photo_id : claim?.document_id}
        cols={cols}
        title={isPhoto ? 'Photos' : 'Documents'}
      />
    </div>
  );
}
